import { FolderOpen, GitBranch, MousePointer2, Network, SlidersHorizontal, type LucideIcon } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { cn } from '@/lib/utils'
import { useStudioLocale } from "@/i18n"
import { getStudioAuthorityEditCopy, getStudioAuthorityTaskCopy } from "@/i18n/studio-authority-copy"
import { getSelectionTitle, useStudioSelectionContext } from './selection-context'
import { useStudioProjectOutputKind } from './components/artifact-mode-switcher'
import { activateStudioAuthorityTask } from './studio-authority-activation'
import { getStudioAuthorityTasks } from './studio-authority-workspaces'
import { COOGO_IDE_CHROME_COPY } from './coogo-ide-chrome-copy'

interface SelectionContextPanelProps {
  onShowChat?: () => void
}

export function SelectionContextPanel({ onShowChat }: SelectionContextPanelProps) {
  const { locale } = useStudioLocale()
  const chromeCopy = COOGO_IDE_CHROME_COPY[locale]
  const outputKind = useStudioProjectOutputKind()
  const selection = useStudioSelectionContext()
  // Progressive disclosure (2026-08-04, user-perspective audit): on DASHBOARD
  // routes this panel used to offer "Edit pages" / "Edit behavior" / "View
  // details" — all three open a workspace surface for an open project — while
  // saying "No selection. Select something in the app." There is no "app" to
  // select in on a dashboard route, so the panel described a world the user
  // was not in. With no project open it now offers the one thing that IS
  // available here: pick the work to open.
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const userProjects = useStudioStore((s) => s.userProjects)
  const openServerProject = useStudioStore((s) => s.openServerProject)
  const hasOpenProject = Boolean(currentProjectId)
  const setInspectorPanelOpen = useStudioStore((s) => s.setInspectorPanelOpen)
  const setResourcesSelection = useStudioStore((s) => s.setResourcesSelection)
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem)
  const setStudioMode = useStudioStore((s) => s.setStudioMode)
  const setViewMode = useStudioStore((s) => s.setViewMode)
  const setIdeActiveTab = useStudioStore((s) => s.setIdeActiveTab)

  const primaryTasks = getStudioAuthorityTasks("primary", outputKind)
  const screenTask = primaryTasks.find((task) => task.railItem === "editor")
  const behaviorTask = primaryTasks.find((task) => task.railItem === "behavior")
  const screenEditCopy = screenTask
    ? getStudioAuthorityEditCopy(locale, getStudioAuthorityTaskCopy(locale, "primary", outputKind, screenTask))
    : null
  const behaviorEditCopy = behaviorTask
    ? getStudioAuthorityEditCopy(locale, getStudioAuthorityTaskCopy(locale, "primary", outputKind, behaviorTask))
    : null

  const showChat = () => {
    setInspectorPanelOpen(false)
    onShowChat?.()
  }

  const openEditorSurface = (railItem: 'editor' | 'behavior' | 'library') => {
    setResourcesSelection(selection)
    setStudioMode('edit')
    setViewMode('editor')
    if (railItem === 'editor') setIdeActiveTab('pages')
    if (railItem === "library") {
      setActiveRailItem(railItem)
      setInspectorPanelOpen(false)
    } else {
      activateStudioAuthorityTask({ domain: "primary", outputKind, railItem })
    }
  }

  const openPageDetails = () => {
    openEditorSurface('editor')
  }

  const openActionFlow = () => {
    openEditorSurface('behavior')
  }

  const openModelDetails = () => {
    openEditorSurface('library')
  }

  return (
    <div data-testid="chat-context-panel" className="flex min-h-0 flex-1 flex-col bg-background">
      <div className="flex h-10 shrink-0 items-center justify-between border-b border-border px-3">
        <span className="text-sm font-semibold text-foreground">{chromeCopy.details}</span>
        <button
          type="button"
          onClick={showChat}
          className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {chromeCopy.showChat}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {hasOpenProject ? (
          <>
            <section className="rounded-md border border-border bg-muted/10 p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                <MousePointer2 className="h-3.5 w-3.5 text-muted-foreground" />
                {getSelectionTitle(selection)}
              </div>
              <p className="mt-2 break-words font-mono text-xs leading-5 text-muted-foreground">
                {selection ? `${selection.label} / ${selection.detail}` : chromeCopy.selectSomething}
              </p>
            </section>

            <div className="mt-3 grid gap-2">
              {screenEditCopy && (
                <ContextCommand
                  icon={SlidersHorizontal}
                  label={screenEditCopy.label}
                  detail={screenEditCopy.description}
                  onClick={openPageDetails}
                />
              )}
              {behaviorEditCopy && (
                <ContextCommand
                  icon={GitBranch}
                  label={behaviorEditCopy.label}
                  detail={behaviorEditCopy.description}
                  onClick={openActionFlow}
                />
              )}
              <ContextCommand
                icon={Network}
                label={chromeCopy.viewDetails}
                detail={chromeCopy.openDeeperModel}
                onClick={openModelDetails}
              />
            </div>

            <div className="mt-3 rounded-md border border-border bg-card/60 p-3 text-xs leading-5 text-muted-foreground">
              {chromeCopy.contextHint}
            </div>
          </>
        ) : (
          <section data-testid="chat-context-pick-target">
            <p className="text-xs leading-5 text-muted-foreground">
              {chromeCopy.pickTargetHint}
            </p>
            {userProjects.length > 0 ? (
              <div className="mt-3 grid gap-2">
                {userProjects.slice(0, 5).map((project) => (
                  <ContextCommand
                    key={project.id}
                    icon={FolderOpen}
                    label={project.name}
                    detail={chromeCopy.openThisWork}
                    testId="chat-context-open-work"
                    onClick={() => {
                      void openServerProject(project.id)
                    }}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-3 rounded-md border border-border bg-card/60 p-3 text-xs leading-5 text-muted-foreground">
                {chromeCopy.noWorkToPick}
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  )
}

function ContextCommand({
  icon: Icon,
  label,
  detail,
  onClick,
  testId,
}: {
  icon: LucideIcon
  label: string
  detail: string
  onClick: () => void
  testId?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className={cn(
        'flex min-h-12 items-start gap-2 rounded-md border border-border bg-background px-3 py-2 text-left transition-colors hover:bg-muted',
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{detail}</span>
      </span>
    </button>
  )
}
